/**
 * Таблица нормативов урожайности — раздел 1 макета
 * `docs/superpowers/prototypes/season-planning-2027/09-yield-norms-screen.html`
 * (AK-789).
 *
 * Вынесена из страницы намеренно: здесь живёт ЕДИНСТВЕННОЕ обещание экрана —
 * «наследование видно глазом». Колонка хозяйства никогда не бывает пустой:
 * либо своя цифра, либо серое «← из компании 26», либо честное «норматива
 * нет». Пустая ячейка означала бы «не знаю», а мы знаем.
 *
 * Компонент без роутера и без запросов — ровно затем, чтобы это обещание
 * проверялось тестом на данных, а не глазами на живом стенде.
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCHa, YIELD_NORM_TAG, type YieldLevel, type YieldNormRow } from '@/lib/planning/yieldDefaults';

/**
 * Колонка хозяйства: цифра, «← из компании N» или честное «норматива нет».
 *
 * Все три случая и само число берутся из СЕРВЕРНОГО итога лестницы
 * (`effectiveLevel` / `effectiveCHa`) — того же кода, что подставляет цифру
 * полю. Записи `row.farm` / `row.company` здесь только адресуют правку и
 * заполняют колонку компании: пересчитывать по ним, кто победил, значило бы
 * держать второе описание правила приоритета, и при расхождении экран соврал
 * бы молча (вердикт head-web по #980).
 */
export function FarmCell({ row }: { row: YieldNormRow }) {
  if (row.status === 'none') {
    return (
      <span className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-destructive">норматива нет</span>
        {row.planEmptyRows > 0 && (
          <Badge variant="outline" className="border-destructive text-destructive">
            {row.planEmptyRows} полей плана без урожайности
          </Badge>
        )}
      </span>
    );
  }
  if (row.status === 'from_company') {
    return (
      <span className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">
          ← из компании {formatCHa(row.effectiveCHa)}
        </span>
        <Badge variant="secondary">{YIELD_NORM_TAG.from_company}</Badge>
      </span>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-semibold tabular-nums text-foreground">
        {formatCHa(row.effectiveCHa)}
      </span>
      <Badge variant={row.status === 'farm_override' ? 'default' : 'secondary'}>
        {YIELD_NORM_TAG[row.status]}
      </Badge>
    </span>
  );
}

/** Подпись действия по строке — на языке решения, а не состояния модели. */
function farmActionLabel(row: YieldNormRow): string {
  if (row.farm) return 'Изменить';
  return row.status === 'none' ? 'Завести норматив' : 'Задать своё для хозяйства';
}

export function YieldNormsTable({
  rows,
  farmName,
  year,
  canUpdate,
  onEdit,
}: {
  rows: readonly YieldNormRow[];
  farmName: string;
  year: number;
  canUpdate: boolean;
  onEdit: (level: YieldLevel, row: YieldNormRow) => void;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Культура</TableHead>
          <TableHead>По умолчанию для компании</TableHead>
          <TableHead>Для хозяйства «{farmName}»</TableHead>
          <TableHead>Сезон</TableHead>
          <TableHead className="text-right">Действие</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.cropId}>
            <TableCell>
              <div className="font-medium text-foreground">{row.cropName}</div>
              {row.inPlan && (
                <div className="text-xs text-muted-foreground">есть в плане {year}</div>
              )}
            </TableCell>
            <TableCell className="tabular-nums text-muted-foreground">
              {row.company ? formatCHa(row.company.cHa) : 'нет'}
            </TableCell>
            <TableCell>
              <FarmCell row={row} />
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {/* Сезон — у ТОЙ записи, что победила в лестнице; какая победила,
                  сказал сервер. */}
              {row.effectiveSeasonScoped === null
                ? '—'
                : row.effectiveSeasonScoped
                  ? `сезон ${year}`
                  : 'любой сезон'}
            </TableCell>
            <TableCell className="text-right">
              {canUpdate ? (
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="sm" onClick={() => onEdit('farm', row)}>
                    {farmActionLabel(row)}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onEdit('company', row)}>
                    По компании
                  </Button>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">только просмотр</span>
              )}
            </TableCell>
          </TableRow>
        ))}
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={5} className="text-sm text-muted-foreground">
              По этому фильтру культур нет.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
